import { type FC, useState } from "react";
import { Box, AppBar, TablePagination, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTagsQuery } from "@/partner/entities/tags";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { TagsListItem } from "./TagsListItem";
import { TagsListToolbar } from "./TagsListToolbar";

interface TagsListProps {
  onEditTag: (id: string) => void;
}

export const TagsList: FC<TagsListProps> = ({ onEditTag }) => {
  const { t } = useTranslation();

  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search, typeId },
  } = useFilter();

  const { data, loading } = useTagsQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        search,
        typeId,
      },
    },
  });

  const tagsData = data?.tags;
  const tags = tagsData?.nodes ?? [];
  const count = tagsData?.count ?? 0;

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(tags.map((t) => ({ id: t.id })));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      setSelectedItems([...selectedItems, { id }]);
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < tags.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tags.length === 0) {
    return <EmptyListCard title={t("tags.list.empty")} />;
  }

  return (
    <Box>
      <AppBar position="static" color="default" elevation={0}>
        <TagsListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={tags}
          onSelectAllClick={handleSelectAll}
          selectedItems={selectedItems}
          onCloseSelected={() => setSelectedItems([])}
        />
      </AppBar>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {tags.map((tag) => (
          <TagsListItem
            key={tag.id}
            tag={tag}
            selected={selectedIds.includes(tag.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEditTag}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
